import { makeObservable, action, runInAction, onBecomeObserved, computed, observable, comparer } from 'mobx'
import mergeMobxListsBy from 'util/mergeMobxListsBy'
import VisitsCollection from '../VisitsCollection'
import makeQuery from 'api/makeQuery'
import PendingVisit from '../visits/PendingVisit'
import { startOfDay } from 'date-fns/startOfDay'
import { endOfDay } from 'date-fns/endOfDay'
import { formatISO } from 'date-fns/formatISO'
import { API_PAST_VISITS_FETCH_LIMIT } from 'constants/apiFetchLimits'
import { isEqual } from 'lodash'
import { parse } from 'date-fns/parse'
import VisitTypes from 'constants/visit-types'

const defaultFilters = {
  memberId: '',
  pendingVisitId: '',
  date: null,

  visitTypes: [],
  providers: [],
  dateRange: null,
}

class PastVisitsCollection extends VisitsCollection {
  constructor(...args) {
    super(...args)
    const { memberId, currentVisitType } = args?.[0] || {}

    this.currentRefetchController = null
    this.currentFilterController = null
    this.serverErrorCount = 0
    this.maxServerErrors = 3
    this.useServerFiltering = true

    this.filters = {
      ...defaultFilters,
      memberId, // Override with provided memberId if available
    }
    this.initialMemberId = memberId
    this.currentVisitType = currentVisitType

    this.memberVisitTypeFilter = args.length && args[0].memberVisitTypeFilter
    this.page = 0
    this.paginationTotal = 0
    this.loading = false
    this.selectedPendingVisitId = ''

    makeObservable(this, {
      endOfPage: computed,
      loading: true,
      page: true,
      mergePendingVisits: action,
      incrementPage: action.bound,
      toggleLoading: action.bound,
      filters: true,
      setFilters: action.bound,
      clearFilters: action.bound,

      serverErrorCount: true,
      useServerFiltering: true,

      hasActiveFilters: computed,
      resetServerErrors: action.bound,
    })

    this.disposePastVisitsFetcher = onBecomeObserved(this, 'list', () => {
      if (this.initialMemberId) {
        this.refetch()
      }
    })
  }

  get hasActiveFilters() {
    return (
      this.filters.visitTypes ||
      this.filters.providers.length > 0 ||
      !!this.filters.dateRange ||
      !!this.filters.memberId ||
      !!this.filters.pendingVisitId ||
      !!this.filters.date
    )
  }

  getClientFilteredList() {
    if (!this.hasActiveFilters) {
      return this.list
    }

    return this.list.filter((visit) => {
      if (this.filters.visitTypes.length > 0) {
        return this.filters.visitTypes.includes(visit.visitType)
      }

      return true
    })
  }

  get canIncrementPage() {
    const notLoading = !this.loading
    const noSelectedVisit = !this.selectedPendingVisitId
    const notAtEnd = !this.endOfPage
    return notLoading && noSelectedVisit && notAtEnd
  }

  destroy() {
    // Cancel any ongoing requests
    if (this.currentRefetchController) {
      this.currentRefetchController.abort()
    }
    if (this.currentFilterController) {
      this.currentFilterController.abort()
    }
    this.disposePastVisitsFetcher()
  }

  setFilters(filters) {
    if (!isEqual(filters, this.filters)) {
      this.filters = filters
      this.page = 0
      this.refetch()
    }
  }

  setPastVisitFilters(filters) {
    if (!isEqual(filters, this.filters)) {
      if (!this.initialMemberId || this.initialMemberId !== this.filters?.memberId) {
        this.initialMemberId = this.filters?.memberId
      }
      this.filters = filters
      this.page = 0
      this.refetch()
    }
  }

  clearFilters() {
    this.filters = { ...defaultFilters }
    this.page = 0
    this.refetch()
  }

  resetServerErrors() {
    this.serverErrorCount = 0
    this.useServerFiltering = true
  }

  buildParams() {
    const { memberId, date, pendingVisitId, visitTypes, providers, dateRange } = this.filters

    const filter = {
      status: ['completed'],
      provider_id: this.initialMemberId ? undefined : this.provider.providerId,
      member_id: memberId || undefined,
      _id: pendingVisitId || undefined,
    }

    if (visitTypes) {
      if (visitTypes === VisitTypes.therapyNow) {
        filter.visit_type = 'therapy'
        filter.scheduled_type = ['now']
      } else if (visitTypes === VisitTypes.therapy) {
        filter.visit_type = visitTypes
        filter.scheduled_type = ['scheduled']
      } else {
        filter.visit_type = visitTypes
      }
    }

    // Add success coaching filter when viewing past visits from a success coaching visit
    // This only applies in member-specific context (Notes copy-forward), not the general dashboard
    if (this.initialMemberId && this.currentVisitType === 'success_coaching') {
      filter.visit_type = 'success_coaching'
    }

    // Add provider filters if specified
    if (providers && providers.length > 0) {
      filter.provider_id = providers
    }

    let params = {
      filter,
      page: {
        skip: this.page,
        limit: API_PAST_VISITS_FETCH_LIMIT,
      },
      sort: {
        scheduled_at: -1,
      },
    }

    if (date) {
      params = {
        ...params,
        gt: {
          scheduled_at: formatISO(startOfDay(parse(date, 'MM-dd-yyyy', new Date()))),
        },
        lt: {
          scheduled_at: formatISO(endOfDay(parse(date, 'MM-dd-yyyy', new Date()))),
        },
      }
    }

    if (dateRange && dateRange.startDate && dateRange.endDate) {
      params = {
        ...params,
        gt: {
          scheduled_at: formatISO(startOfDay(dateRange.startDate)),
        },
        lt: {
          scheduled_at: formatISO(endOfDay(dateRange.endDate)),
        },
      }
    }

    return params
  }

  incrementPage() {
    this.page = this.page + API_PAST_VISITS_FETCH_LIMIT
    this.refetch()
  }

  toggleLoading() {
    this.loading = !this.loading
  }

  mergePendingVisits(pendingVisits, shouldDelete) {
    mergeMobxListsBy(this.map, this.list, pendingVisits, 'pendingVisitId', (v) => new PendingVisit(v), shouldDelete)
    this.list.sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt))
  }

  async refetch() {
    if (this.hasActiveFilters && (!this.useServerFiltering || this.serverErrorCount >= this.maxServerErrors)) {
      console.warn('Server filtering disabled due to errors, falling back to client-side filtering')
      return
    }

    if (this.currentRefetchController) {
      this.currentRefetchController.abort()
    }
    if (this.currentFilterController) {
      this.currentFilterController.abort()
    }

    this.currentRefetchController = new AbortController()
    const signal = this.currentRefetchController.signal

    this.toggleLoading()

    const params = this.buildParams()

    let result

    try {
      result = await makeQuery('getPendingVisits', params, { signal })

      // Reset error count on successful request
      if (this.serverErrorCount > 0) {
        this.resetServerErrors()
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was aborted, don't update state
        return
      }

      // Increment error count for server filtering errors
      if (this.hasActiveFilters) {
        this.serverErrorCount++
        if (this.serverErrorCount >= this.maxServerErrors) {
          this.useServerFiltering = false
          console.error(`Server filtering disabled after ${this.maxServerErrors} consecutive errors`)
        }
        console.warn('Server filtering failed, showing empty results:', error)
      }

      result = {
        paginated: [],
        meta: {
          results: 0,
        },
      }
    }

    runInAction(() => {
      this.toggleLoading()
      this.paginationTotal = result?.meta?.results || result?.paginated?.length || 0
      this.mergePendingVisits(result?.paginated, this.page === 0)
      this.currentRefetchController = null
      this.currentFilterController = null
    })
  }

  get endOfPage() {
    return this.list.length >= this.paginationTotal
  }
}

export default PastVisitsCollection
